import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  Activity, ChevronLeft, ChevronRight, FileArchive, FileText, FolderClosed, FolderOpen, FolderPlus,
  Image as ImageIcon, LogOut, Moon, Music, MoreHorizontal, Search, Settings,
  Sparkles, Sun, Tag, Trash2, Upload, Users, Video, X, PlayCircle, Download,
} from 'lucide-react';
import type { Folder } from '@shared/types';
import { openTraining, useTrainingManifest } from '@/components/TrainingPopup';
import { downloadFolderZip, uploadFiles } from '@/lib/zip-transfer';
import { cn } from '@/lib/format';
import { keepBrowseFilters } from '@/lib/browse';
import {
  RAIL_FILES_PER_PAGE,
  useCreateFolderAt, useDeleteFolder, useFacets, useFolderFiles, useRestore,
  useFolders, useLogout, useMe, useMoveAsset, useMoveAssets, useRenameFolder,
  useUpload,
} from '@/lib/queries';
import { isAssetDrag, readAssetDrag, setAssetDrag } from '@/lib/drag';
import { useSelection } from '@/lib/selection';
import { useToast } from './Toasts';
import { useTheme } from '@/lib/theme';
import { t } from '@/lib/i18n';
import { RailSearch } from './RailSearch';
import { Spinner } from './ui';

/**
 * Left rail — the DAM's spine. Folders come first and are always open: they're
 * what people actually navigate by, and the tree expands all the way down to
 * individual files. The rest (Library smart views, Browse facets, Manage) sits
 * below in sections that stay collapsed until asked for, so the rail opens on
 * structure rather than on a wall of links. Counts come from /library/stats.
 */
export function Sidebar({ width }: { width?: number }) {
  const { data: folderData, isLoading } = useFolders();
  const { data: me } = useMe();
  const { theme, toggle } = useTheme();
  const logout = useLogout();
  const training = useTrainingManifest();

  return (
    <nav
      className="flex h-full shrink-0 flex-col bg-brand-900 text-brand-50/80"
      style={{ width: width ?? 288 }}
      aria-label={t('sidebar.navigation')}
    >
      <div className="flex h-14 items-center border-b border-white/10 px-3">
        {/* The logo is the way home, as it is on most sites. The white plate is
            the link itself so the whole badge is the target, not just the
            artwork inside it. */}
        <Link
          to="/"
          aria-label={t('sidebar.goToHome')}
          title={t('sidebar.home')}
          className="flex items-center rounded-lg bg-white px-2.5 py-1.5 transition-shadow hover:shadow-md hover:shadow-black/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
        >
          <img src="/aicotone-logo.png" alt="AIcotone" className="h-7 w-auto" />
        </Link>
      </div>

      {/* Sits above the scroll area so it stays reachable however far down the
          folder tree is scrolled. */}
      <RailSearch />

      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto pb-3">
        {/* ---- Folders: the primary way in ---- */}
        <FolderSection folders={folderData?.tree ?? []} loading={isLoading} />

        {/* Library views (All, Images, PDFs, Artwork, Share Basket, …) are the
            tab bar across the top of the main area (TopTabs). */}

        {/* ---- Browse: AI facets ---- */}
        <BrowseSection />

        {/* ---- Manage (admin) ---- */}
        {me?.isAdmin && (
          <SectionPanel title={t('sidebar.manage')} icon={<Settings />}>
            <NavItem to="/review" icon={<Sparkles />} label={t('sidebar.tagReview')} />
            <NavItem to="/prompt" icon={<Tag />} label={t('sidebar.taggingPrompt')} />
            <NavItem to="/users" icon={<Users />} label={t('sidebar.users')} />
            <NavItem to="/activity" icon={<Activity />} label={t('sidebar.activity')} />
            <NavItem to="/settings" icon={<Settings />} label={t('sidebar.settings')} />
          </SectionPanel>
        )}
      </div>

      {training && (
      <button
        type="button"
        onClick={openTraining}
        className="flex items-center gap-2 border-t border-white/10 px-4 py-2 text-left text-xs text-white/60 hover:bg-white/10 hover:text-white"
      >
        <PlayCircle className="size-3.5" /> {t('training.watch')}
      </button>
      )}


      {/* footer */}
      <div className="flex items-center gap-2 border-t border-white/10 p-2">
        <Link
          to="/account"
          className="min-w-0 flex-1 truncate rounded px-2 py-1 text-xs text-white/80 hover:bg-white/10 hover:text-white [&.active]:text-white"
          title={t('sidebar.account')}
        >
          {me?.name ?? '…'}
          {me?.isAdmin && <span className="ml-1.5 rounded bg-white/15 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white/70">{t('sidebar.admin')}</span>}
        </Link>
        <button onClick={toggle} className="grid size-7 place-items-center rounded text-white/50 hover:bg-white/10 hover:text-white" aria-label={t('sidebar.toggleTheme')}>
          {theme === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
        </button>
        <button
          onClick={() => logout.mutate(undefined, { onSettled: () => window.location.assign('/login') })}
          className="grid size-7 place-items-center rounded text-white/50 hover:bg-white/10 hover:text-white"
          aria-label={t('sidebar.signOut')}
        >
          <LogOut className="size-4" />
        </button>
      </div>
    </nav>
  );
}

function Section({ title, right }: { title: string; right?: React.ReactNode }) {
  return (
    <div className="mt-4 flex items-center justify-between px-4 pb-1 first:mt-2">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-white/40">{title}</span>
      {right}
    </div>
  );
}

/**
 * A collapsible rail section. `defaultOpen` sets the first-load state; the
 * user's own choice is remembered per section and always wins over it, so
 * reopening (or closing) a section survives navigation — each page mounts a
 * fresh Sidebar.
 */
function SectionPanel({
  title, icon, children, defaultOpen = false,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const storageKey = `dam.rail.${title.toLowerCase()}`;
  const [open, setOpen] = useState(() => {
    try {
      const saved = window.localStorage.getItem(storageKey);
      // Only an explicit stored choice overrides the default — otherwise a
      // section the user has never touched would read as deliberately closed.
      return saved === null ? defaultOpen : saved === 'open';
    } catch {
      return defaultOpen; // private mode / storage disabled
    }
  });

  function toggle() {
    setOpen((v) => {
      try {
        window.localStorage.setItem(storageKey, v ? 'closed' : 'open');
      } catch { /* non-fatal: the section still toggles for this session */ }
      return !v;
    });
  }

  return (
    <div className="mt-3 px-2 first:mt-2">
      <button
        onClick={toggle}
        aria-expanded={open}
        className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-white/40 transition-colors hover:bg-white/10 hover:text-white/70 [&>svg]:size-3.5 [&>svg]:shrink-0"
      >
        {icon}
        <span className="flex-1 text-left">{title}</span>
        <ChevronRight className={cn('size-3.5 transition-transform', open && 'rotate-90')} />
      </button>
      {open && <div className="mt-0.5 space-y-0.5">{children}</div>}
    </div>
  );
}

const navBase =
  'flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-[13px] transition-colors hover:bg-white/10 hover:text-white [&>svg]:size-4 [&>svg]:shrink-0';

function NavItem({
  to,
  icon,
  label,
  count,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  /** Optional badge — used by the basket so its contents are visible anywhere. */
  count?: number;
}) {
  return (
    <Link to={to} className={cn(navBase, '[&.active]:bg-white/20 [&.active]:text-white')}>
      {icon}
      <span className="truncate">{label}</span>
      {count ? (
        <span className="ml-auto rounded-full bg-brand-500 px-1.5 py-0.5 text-[10px] font-medium text-white">
          {count}
        </span>
      ) : null}
    </Link>
  );
}

/**
 * A Library smart-view link. All point at "/" with different search params; the
 * BrowsePage reads them. Active state is computed from the live location so only
 * the matching view highlights.
 */
/* ---------- Folders section (editable tree) ---------- */

function FolderSection({ folders, loading }: { folders: Folder[]; loading: boolean }) {
  const createFolder = useCreateFolderAt();
  return (
    <>
      <Section
        title={t('sidebar.folders')}
        right={
          <button
            onClick={() => {
              const name = window.prompt(t('sidebar.newTopLevelFolderPrompt'));
              if (name?.trim()) createFolder.mutate({ name: name.trim(), parentId: null });
            }}
            className="grid size-5 place-items-center rounded text-white/40 hover:bg-white/10 hover:text-white"
            title={t('sidebar.newFolder')}
            aria-label={t('sidebar.newTopLevelFolder')}
          >
            <FolderPlus className="size-3.5" />
          </button>
        }
      />
      <div className="px-2">
        {loading ? (
          <div className="flex justify-center py-4"><Spinner className="text-white/40" /></div>
        ) : folders.length === 0 ? (
          <p className="px-2 py-2 text-[11px] text-white/30">{t('sidebar.noFoldersYet')}</p>
        ) : (
          folders.map((f) => <FolderNode key={f.id} folder={f} depth={0} />)
        )}
      </div>
    </>
  );
}

/**
 * Dwell before a hovered folder springs open. Long enough that crossing a row
 * on the way past does not expand it; short enough not to feel stuck.
 */
const SPRING_MS = 600;

/** Files dragged in from the desktop (an upload), as opposed to one of our assets. */
function isFileDrag(e: React.DragEvent): boolean {
  return e.dataTransfer.types.includes('Files');
}

/**
 * Left indent for a tree row at this depth. Full steps for the first five
 * levels, then smaller ones: the rail is narrow, but a flat cap made a subfolder
 * line up with its parent (e.g. /INTERNAL/DE/BRANDS/ALLOS/Category/Aufstriche/Aufs Brot).
 */
function indent(depth: number): number {
  return Math.min(depth, 5) * 1.1 + Math.max(0, Math.min(depth, 12) - 5) * 0.7;
}

function FolderNode({ folder, depth }: { folder: Folder; depth: number }) {
  const children = folder.children ?? [];
  // A node is expandable if it holds subfolders OR files — the tree goes all the
  // way down to individual assets, so a leaf folder with files still gets a caret.
  const hasFiles = (folder.assetCount ?? 0) > 0;
  const hasChildren = children.length > 0 || hasFiles;
  // Top-level folders start open so their immediate children are visible, but
  // only one level deep — a node at depth 1+ stays closed until asked for, so
  // the rail shows structure without unfurling a whole subtree.
  const [open, setOpen] = useState(depth === 0);
  const [menu, setMenu] = useState(false);
  // Files appear only once the user opens the node themselves. The first level
  // is expanded automatically to reveal *subfolders*; pulling every file in
  // with it would put 1,400 rows under PRODUCT_IMAGES before anyone asked.
  const [userOpened, setUserOpened] = useState(false);
  const showFiles = open && userOpened && hasFiles;
  // Per-folder file filter and page. Both live on the node, so two open folders
  // keep their own position rather than sharing one.
  const [fileQuery, setFileQuery] = useState('');
  // "Search in this folder": finds subfolders at any depth below this one only.
  // null = closed; '' = open, nothing typed yet.
  const [folderQuery, setFolderQuery] = useState<string | null>(null);
  const folderMatches = (() => {
    const q = (folderQuery ?? '').trim().toLowerCase();
    if (!q) return [];
    const out: { folder: Folder; rel: string }[] = [];
    const walk = (list: Folder[], prefix: string) => {
      for (const f of list) {
        const rel = prefix ? `${prefix}/${f.name}` : f.name;
        if (f.name.toLowerCase().includes(q)) out.push({ folder: f, rel });
        if (out.length >= 200) return;
        if (f.children?.length) walk(f.children, rel);
      }
    };
    walk(children, '');
    return out;
  })();
  const [page, setPage] = useState(1);

  // Debounced so typing doesn't fire a request per keystroke.
  const [debouncedQuery, setDebouncedQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(fileQuery), 250);
    return () => clearTimeout(t);
  }, [fileQuery]);

  // A changed filter re-pages from the start: page 7 of the old result set is
  // meaningless against the new one, and often past its end.
  useEffect(() => setPage(1), [debouncedQuery]);

  // Collapsing a folder discards its filter, so reopening it shows the folder
  // rather than the last search — which, with the box hidden while closed,
  // would otherwise look like files had gone missing.
  useEffect(() => {
    if (!open) {
      setFileQuery('');
      setPage(1);
    }
  }, [open]);

  const { data: files, isLoading: filesLoading, isFetching: filesFetching } = useFolderFiles(
    folder.id,
    showFiles,
    { page, search: debouncedQuery },
  );
  const navigate = useNavigate();
  // The row is a button now, so the router's automatic `.active` class is gone —
  // derive the highlight from the live location instead.
  const selected = useRouterState().location.pathname === `/folders/${folder.id}`;
  const createFolder = useCreateFolderAt();
  const rename = useRenameFolder();
  const del = useDeleteFolder();
  const restore = useRestore();
  const upload = useUpload(folder.id);
  const move = useMoveAsset();
  const moveMany = useMoveAssets();
  const toast = useToast();
  const selection = useSelection();
  const me = useMe().data;
  // Highlights while an asset (a move) or files from the desktop (an upload
  // into this folder) are over this row.
  const [dropTarget, setDropTarget] = useState(false);
  const [dragging, setDragging] = useState(false);
  /**
   * Spring-loaded expansion: hovering a collapsed folder mid-drag opens it, so
   * a deep destination can be reached without dropping and starting again.
   * Held in a ref because `onDragOver` fires continuously — restarting the
   * timer on every event would mean it never elapses.
   */
  const springTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function cancelSpring() {
    if (springTimer.current) {
      clearTimeout(springTimer.current);
      springTimer.current = null;
    }
  }

  // Never leave a timer running when the row goes away mid-drag.
  useEffect(() => cancelSpring, []);

  function onDragOver(e: React.DragEvent) {
    const asset = isAssetDrag(e);
    if (!asset && !isFileDrag(e)) return;
    // While dragging this very folder, it is not a target for itself.
    if (asset && dragging) return;
    // Preventing default is what marks this a valid drop target.
    e.preventDefault();
    e.dataTransfer.dropEffect = asset ? 'move' : 'copy';
    setDropTarget(true);

    // Arm the spring once per hover. A short dwell distinguishes "heading
    // somewhere deeper" from merely passing over a row on the way down the
    // tree, which would otherwise unfurl every folder the cursor crosses.
    if (hasChildren && !open && !springTimer.current) {
      springTimer.current = setTimeout(() => {
        springTimer.current = null;
        setOpen(true);
        // Reveal files too: the point of springing open is to expose the next
        // level, and a folder holding only files would otherwise appear empty.
        setUserOpened(true);
      }, SPRING_MS);
    }
  }

  function onDrop(e: React.DragEvent) {
    // Files from the desktop upload straight into this folder, as in the old DAM.
    if (!isAssetDrag(e) && isFileDrag(e)) {
      e.preventDefault();
      e.stopPropagation(); // a nested folder must not also receive this drop
      setDropTarget(false);
      cancelSpring();
      const dropped = Array.from(e.dataTransfer.files);
      if (dropped.length) void uploadFiles(dropped, folder.id, (f) => upload.mutateAsync(f), (message) => toast.push({ message, tone: 'error' }));
      return;
    }
    const payload = readAssetDrag(e);
    if (!payload) return;
    e.preventDefault();
    e.stopPropagation(); // a nested folder must not also receive this drop
    setDropTarget(false);
    cancelSpring();
    // Dropping an asset back where it already lives is a no-op, not an error.
    if (payload.folderId === folder.id) return;

    const from = payload.folderId;
    const ids = payload.ids?.length ? payload.ids : [payload.id];
    const label = ids.length === 1 ? payload.fileName : t('sidebar.nItems', { n: ids.length });

    const done = () => {
      selection.clear();
      toast.push({
        message: t('sidebar.movedToFolder', { label, folder: folder.name }),
        // Undo is only offered when we know where it came from. A drag that
        // started without a source folder (root-level) has nowhere to go back
        // to, and a wrong guess would be worse than no button.
        onUndo:
          from === null
            ? undefined
            : async () => {
                await moveMany.mutateAsync({ ids, folderId: from });
              },
      });
    };

    if (ids.length === 1) {
      move.mutate({ id: ids[0]!, folderId: folder.id }, { onSuccess: done });
    } else {
      moveMany.mutate({ ids, folderId: folder.id }, { onSuccess: done });
    }
  }

  function onUpload() {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.onchange = () => {
      const files = input.files;
      if (files) void uploadFiles(Array.from(files), folder.id, (f) => upload.mutateAsync(f), (message) => toast.push({ message, tone: 'error' }));
    };
    input.click();
    setMenu(false);
  }

  return (
    <div>
      <div
        className={cn(
          'group relative flex items-center rounded-lg hover:bg-white/10',
          dropTarget && 'bg-brand-500/30 ring-1 ring-inset ring-brand-300',
        )}
        style={{ paddingLeft: `${indent(depth)}rem` }}
        // A folder is an asset in Pimcore, so it moves through the same
        // endpoint and can be dragged like one. The server refuses to move a
        // folder into its own subtree, which would orphan it.
        draggable
        onDragStart={(e) => {
          e.stopPropagation();
          setDragging(true);
          setAssetDrag(e, { id: folder.id, fileName: folder.name, folderId: folder.parentId ?? null });
        }}
        onDragEnd={() => setDragging(false)}
        onDragOver={onDragOver}
        onDragLeave={() => { setDropTarget(false); cancelSpring(); }}
        onDrop={onDrop}
      >
        {hasChildren ? (
          // A real button, not decoration. It was an aria-hidden span outside
          // the row button, so the obvious place to click to expand a folder —
          // the chevron itself — did nothing.
          //
          // It only expands/collapses; it deliberately does NOT navigate, so
          // the tree can be explored without changing what's shown on the right.
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (open && !userOpened && hasFiles) setUserOpened(true);
              else {
                setOpen((v) => !v);
                setUserOpened(true);
              }
            }}
            aria-expanded={open}
            aria-label={open ? t('sidebar.collapseFolder', { name: folder.name }) : t('sidebar.expandFolder', { name: folder.name })}
            title={open ? t('sidebar.collapse') : t('sidebar.expand')}
            className="grid size-6 shrink-0 place-items-center rounded text-white/40 transition-colors hover:bg-white/15 hover:text-white"
          >
            <ChevronRight className={cn('size-3.5 transition-transform', open && 'rotate-90')} />
          </button>
        ) : (
          <span className="size-6 shrink-0" />
        )}
        {/* The whole row toggles the node AND selects it, so one click both
            expands the tree and shows the folder's contents on the right.
            Navigation is done imperatively because a <Link> would swallow the
            click before the toggle could run. */}
        <button
          onClick={() => {
            if (hasChildren) {
              // An auto-opened top-level node has never had its files shown, so
              // the first click reveals them rather than collapsing a node the
              // user never opened. After that it toggles normally.
              if (open && !userOpened && hasFiles) setUserOpened(true);
              else {
                setOpen((v) => !v);
                setUserOpened(true);
              }
            }
            void navigate({ to: '/folders/$folderId', params: { folderId: folder.id }, search: keepBrowseFilters as never });
          }}
          // No aria-expanded here: the chevron button beside it owns the
          // expanded state, and two elements reporting it for one node is
          // ambiguous. This one opens the folder; that one expands it.
          className={cn(
            'flex min-w-0 flex-1 items-center gap-2 py-1.5 text-left text-[13px] hover:text-white',
            selected && 'font-medium text-white',
          )}
          title={folder.path}
        >
          {open ? <FolderOpen className="size-3.5 shrink-0 text-white/50" /> : <FolderClosed className="size-3.5 shrink-0 text-white/50" />}
          <span className="truncate">{folder.name}</span>
          {folder.assetCount ? <span className="text-[10px] text-white/40 tabular-nums">{folder.assetCount}</span> : null}
        </button>
        <button
          onClick={() => setMenu((v) => !v)}
          className="mr-1 hidden size-6 shrink-0 place-items-center rounded text-white/40 hover:bg-white/15 hover:text-white group-hover:grid data-[open=true]:grid"
          data-open={menu}
          aria-label={t('sidebar.folderActions')}
        >
          <MoreHorizontal className="size-3.5" />
        </button>

        {menu && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setMenu(false)} />
            <div className="absolute right-1 top-7 z-20 w-44 overflow-hidden rounded-lg border border-white/10 bg-brand-800 py-1 text-[13px] text-white/90 shadow-xl">
              <MenuBtn icon={<Upload className="size-3.5" />} label={t('sidebar.uploadHere')} onClick={onUpload} />
              <MenuBtn
                icon={<Download className="size-3.5" />}
                label={t('zip.downloadFolder')}
                onClick={() => {
                  setMenu(false);
                  void downloadFolderZip(folder.id, folder.name, (message) => toast.push({ message, tone: 'error' }));
                }}
              />
              {children.length > 0 && (
                <MenuBtn
                  icon={<Search className="size-3.5" />}
                  label={t('sidebar.searchInFolder')}
                  onClick={() => {
                    setMenu(false);
                    setOpen(true);
                    setFolderQuery('');
                  }}
                />
              )}
              <MenuBtn
                icon={<FolderPlus className="size-3.5" />}
                label={t('sidebar.newSubfolder')}
                onClick={() => {
                  const name = window.prompt(t('sidebar.newSubfolderPrompt', { name: folder.name }));
                  if (name?.trim()) createFolder.mutate({ name: name.trim(), parentId: folder.id });
                  setMenu(false);
                }}
              />
              <MenuBtn
                icon={<Tag className="size-3.5" />}
                label={t('sidebar.rename')}
                onClick={() => {
                  const name = window.prompt(t('sidebar.renameFolderPrompt'), folder.name);
                  if (name?.trim() && name.trim() !== folder.name) rename.mutate({ id: folder.id, name: name.trim() });
                  setMenu(false);
                }}
              />
              {me?.isAdmin && (
                <MenuBtn
                  icon={<Trash2 className="size-3.5" />}
                  label={t('sidebar.delete')}
                  danger
                  onClick={() => {
                    // The confirm stays: a folder delete cascades to every
                    // descendant, so it deserves a deliberate step even with an
                    // undo behind it. What changed is the promise — it is no
                    // longer "cannot be undone", because now it usually can be.
                    if (window.confirm(t('sidebar.deleteFolderConfirm', { name: folder.name }))) {
                      del.mutate(folder.id, {
                        onSuccess: (r) => {
                          toast.push({
                            message: t('sidebar.folderDeleted', { name: folder.name }),
                            // Undo only when the recycle-bin snapshot exists.
                            // A null handle means this delete is permanent, and
                            // offering a button that would fail is worse than
                            // offering none.
                            onUndo:
                              r.recycleBinId == null
                                ? undefined
                                : async () => {
                                    await restore.mutateAsync(r.recycleBinId as number);
                                  },
                          });
                        },
                      });
                    }
                    setMenu(false);
                  }}
                />
              )}
            </div>
          </>
        )}
      </div>
      {open && hasChildren && (
        <div>
          {folderQuery !== null && (
            <div className="py-0.5" style={{ paddingLeft: `${indent(depth + 1) + 0.5}rem`, paddingRight: '0.5rem' }}>
              <div className="relative">
                <Search className="pointer-events-none absolute left-1.5 top-1/2 size-3 -translate-y-1/2 text-white/40" />
                <input
                  autoFocus
                  value={folderQuery}
                  onChange={(e) => setFolderQuery(e.target.value)}
                  placeholder={t('sidebar.searchFoldersIn', { name: folder.name })}
                  aria-label={t('sidebar.searchFoldersIn', { name: folder.name })}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      e.stopPropagation();
                      setFolderQuery(null);
                    }
                  }}
                  className="w-full rounded bg-white/15 py-1 pl-6 pr-5 text-[12px] text-white placeholder:text-white/40 outline-none ring-1 ring-white/25 focus:bg-white/20"
                />
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setFolderQuery(null); }}
                  aria-label={t('sidebar.clearFilter')}
                  className="absolute right-1 top-1/2 grid size-4 -translate-y-1/2 place-items-center rounded text-white/50 hover:bg-white/10 hover:text-white"
                >
                  <X className="size-2.5" />
                </button>
              </div>
              {folderQuery.trim() !== '' && (
                <div className="mt-0.5 max-h-80 overflow-y-auto">
                  {folderMatches.length === 0 && (
                    <p className="px-2 py-1 text-[11px] text-white/40">{t('sidebar.noFoldersMatch')}</p>
                  )}
                  {folderMatches.map(({ folder: f, rel }) => (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => void navigate({ to: '/folders/$folderId', params: { folderId: f.id }, search: keepBrowseFilters as never })}
                      title={f.path}
                      className="flex w-full items-center gap-1.5 rounded px-2 py-1 text-left text-[12px] text-white/80 hover:bg-white/10 hover:text-white"
                    >
                      <FolderClosed className="size-3.5 shrink-0 text-white/40" />
                      <span className="min-w-0 truncate">{rel}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {(folderQuery ?? '').trim() === '' && children.map((c) => <FolderNode key={c.id} folder={c} depth={depth + 1} />)}
          {/* Files sit below subfolders, mirroring how a file manager orders them. */}
          {/* Filter box, shown when the FOLDER holds more than a page.
              Deliberately keyed off the folder's own size, never the result
              total: filtering 1,400 files down to 3 drops the total below the
              threshold, which used to unmount this box mid-search and strip out
              the only way to clear or change the term. `|| fileQuery` keeps it
              mounted whenever a filter is active, however few rows match. */}
          {showFiles && ((folder.assetCount ?? 0) > RAIL_FILES_PER_PAGE || fileQuery !== '') && (
            <div className="py-0.5" style={{ paddingLeft: `${indent(depth + 1) + 1.5}rem`, paddingRight: '0.5rem' }}>
              <div className="relative">
                <Search className="pointer-events-none absolute left-1.5 top-1/2 size-3 -translate-y-1/2 text-white/30" />
                <input
                  value={fileQuery}
                  onChange={(e) => setFileQuery(e.target.value)}
                  // The folder's own count, not the result total — the label is
                  // what you can filter, not what is left after filtering. Also
                  // avoids a non-null assertion on `files`, which is undefined
                  // on the first render with a filter already applied.
                  placeholder={t('sidebar.filterFiles', { count: (folder.assetCount ?? 0).toLocaleString() })}
                  aria-label={t('sidebar.filterFilesIn', { name: folder.name })}
                  // The row above is a link; a click here must not navigate.
                  onClick={(e) => e.stopPropagation()}
                  // Escape clears without reaching for the ✕.
                  onKeyDown={(e) => {
                    if (e.key === 'Escape' && fileQuery) {
                      e.stopPropagation();
                      setFileQuery('');
                    }
                  }}
                  className="w-full rounded bg-white/10 py-0.5 pl-6 pr-5 text-[11px] text-white placeholder:text-white/30 outline-none focus:bg-white/15 focus:ring-1 focus:ring-white/25"
                />
                {fileQuery && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setFileQuery(''); }}
                    aria-label={t('sidebar.clearFilter')}
                    className="absolute right-1 top-1/2 grid size-4 -translate-y-1/2 place-items-center rounded text-white/40 hover:bg-white/10 hover:text-white"
                  >
                    <X className="size-2.5" />
                  </button>
                )}
              </div>
            </div>
          )}
          {showFiles && filesLoading && (
            <div className="py-1.5" style={{ paddingLeft: `${indent(depth + 1) + 1.5}rem` }}>
              <Spinner className="size-3 text-white/30" />
            </div>
          )}
          {showFiles && files?.items.map((a) => <FileNode key={a.id} asset={a} depth={depth + 1} folderId={folder.id} />)}
          {/* A filter that matches nothing needs saying — otherwise the folder
              just looks empty. */}
          {showFiles && !filesLoading && files && files.items.length === 0 && (
            <div
              className="flex flex-wrap items-center gap-x-1.5 py-1 text-[11px] text-white/40"
              style={{ paddingLeft: `${indent(depth + 1) + 1.5}rem` }}
            >
              <span className="truncate">{t('sidebar.noFilesMatch', { query: debouncedQuery })}</span>
              {/* A second way out, next to the thing that went wrong. */}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setFileQuery(''); }}
                className="shrink-0 text-white/60 underline underline-offset-2 hover:text-white"
              >
                {t('sidebar.clear')}
              </button>
            </div>
          )}
          {showFiles && files && files.totalPages > 1 && (
            <FilePager
              page={files.page}
              totalPages={files.totalPages}
              total={files.total}
              busy={filesFetching}
              depth={depth + 1}
              onPage={setPage}
            />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Prev/next pager for one folder's files.
 *
 * Deliberately not numbered page links: the rail is ~290px wide and a folder
 * can run to 28 pages, so a row of numbers would wrap into a block taller than
 * the files it pages through.
 */
function FilePager({
  page,
  totalPages,
  total,
  busy,
  depth,
  onPage,
}: {
  page: number;
  totalPages: number;
  total: number;
  busy: boolean;
  depth: number;
  onPage: (p: number) => void;
}) {
  const step = (to: number) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onPage(Math.min(totalPages, Math.max(1, to)));
  };

  const btn =
    'grid size-5 place-items-center rounded text-white/50 transition-colors hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent';

  return (
    <div
      className="flex items-center gap-1 py-1 text-[10px] text-white/40"
      style={{ paddingLeft: `${indent(depth) + 1.5}rem`, paddingRight: '0.5rem' }}
    >
      <button type="button" onClick={step(page - 1)} disabled={page <= 1} aria-label={t('sidebar.previousPage')} className={btn}>
        <ChevronLeft className="size-3" />
      </button>
      <span className="tabular-nums">
        {page}/{totalPages}
      </span>
      <button type="button" onClick={step(page + 1)} disabled={page >= totalPages} aria-label={t('sidebar.nextPage')} className={btn}>
        <ChevronRight className="size-3" />
      </button>
      <span className="ml-auto tabular-nums">{total.toLocaleString()}</span>
      {busy && <Spinner className="size-2.5 text-white/30" />}
    </div>
  );
}

/** Icon for a file leaf, matched to the asset kind. */
const KIND_ICONS: Record<string, typeof FileText> = {
  image: ImageIcon,
  document: FileText,
  video: Video,
  audio: Music,
  archive: FileArchive,
};

/** A single asset in the tree — the deepest level a user can expand to. */
/**
 * A file row in the tree. Draggable onto any folder, so an asset can be moved
 * without first opening the folder that holds it — the rail alone is enough.
 * `folderId` is passed in because the row itself only knows the asset.
 */
function FileNode({
  asset,
  depth,
  folderId,
}: {
  asset: { id: number; fileName: string; kind: string };
  depth: number;
  folderId: number;
}) {
  const Icon = KIND_ICONS[asset.kind] ?? FileText;
  return (
    <Link
      to="/assets/$assetId"
      params={{ assetId: asset.id }}
      draggable
      onDragStart={(e) => setAssetDrag(e, { id: asset.id, fileName: asset.fileName, folderId })}
      className="flex items-center gap-2 rounded-lg py-1 text-[12px] text-white/60 hover:bg-white/10 hover:text-white [&.active]:bg-white/20 [&.active]:text-white"
      style={{ paddingLeft: `${indent(depth) + 1.5}rem` }}
      title={asset.fileName}
    >
      <Icon className="size-3.5 shrink-0 text-white/40" />
      <span className="truncate pr-2">{asset.fileName}</span>
    </Link>
  );
}

function MenuBtn({ icon, label, onClick, danger }: { icon: React.ReactNode; label: string; onClick: () => void; danger?: boolean }) {
  return (
    <button
      onClick={onClick}
      className={cn('flex w-full items-center gap-2.5 px-3 py-1.5 text-left hover:bg-white/10', danger && 'text-red-300 hover:bg-red-500/20')}
    >
      {icon}
      {label}
    </button>
  );
}

/* ---------- Browse section: AI brand / image-type facets ---------- */

function BrowseSection() {
  const { data: facets } = useFacets();
  const [openB, setOpenB] = useState(false);
  const [openT, setOpenT] = useState(false);
  const brands = facets?.brands ?? [];
  const types = facets?.imageTypes ?? [];
  if (brands.length === 0 && types.length === 0) return null;

  return (
    <SectionPanel title={t('sidebar.browse')} icon={<Tag />} defaultOpen>
      {brands.length > 0 && (
        <Collapsible label={t('sidebar.brands')} open={openB} onToggle={() => setOpenB((v) => !v)}>
          {brands.map((b) => (
            <FacetLink key={b} label={b} search={{ brand: b }} />
          ))}
        </Collapsible>
      )}
      {types.length > 0 && (
        <Collapsible label={t('sidebar.imageTypes')} open={openT} onToggle={() => setOpenT((v) => !v)}>
          {types.map((tp) => (
            <FacetLink key={tp.value} label={tp.value} search={{ imageType: tp.value }} />
          ))}
        </Collapsible>
      )}
    </SectionPanel>
  );
}

function Collapsible({ label, open, onToggle, children }: { label: string; open: boolean; onToggle: () => void; children: React.ReactNode }) {
  return (
    <div>
      <button onClick={onToggle} className={cn(navBase, 'w-full text-white/70')}>
        <ChevronRight className={cn('transition-transform', open && 'rotate-90')} />
        <span className="flex-1 text-left">{label}</span>
      </button>
      {open && <div className="ml-3 space-y-0.5 border-l border-white/10 pl-2">{children}</div>}
    </div>
  );
}

function FacetLink({ label, search }: { label: string; search: Record<string, string> }) {
  return (
    <Link
      to="/search"
      search={search}
      className="block truncate rounded px-2.5 py-1 text-[12px] text-white/60 hover:bg-white/10 hover:text-white"
      title={label}
    >
      {label}
    </Link>
  );
}
